(function (window, document) {
  'use strict';

  let currentSquareFeet = 0;
  let lastVariantId = '';
  let lastUnitPrice = null;
  let pricingData = {
    selectedVariantId: '',
    variantPrices: {}
  };

  function getPricingRoot() {
    return document.querySelector('[data-aw-pricing]');
  }

  function loadPricingData() {
    const root = getPricingRoot();
    const script = root?.querySelector('[data-aw-pricing-data]');

    if (!script) return;

    try {
      const parsed = JSON.parse(script.textContent || '{}');

      pricingData = {
        selectedVariantId: parsed.selectedVariantId
          ? String(parsed.selectedVariantId)
          : '',
        variantPrices: parsed.variantPrices || {}
      };

      if (!lastVariantId && pricingData.selectedVariantId) {
        lastVariantId = pricingData.selectedVariantId;
      }
    } catch (error) {
      console.warn('Unable to parse Alwan variant pricing data.', error);
    }
  }

  function getProductForm() {
    const root = getPricingRoot();
    const formId = root?.dataset.awProductFormId;

    return (
      (formId ? document.getElementById(formId) : null) ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function getCurrentVariantId() {
    const form = getProductForm();

    const checked = form?.querySelector('input[name="id"]:checked');
    if (checked?.value) return String(checked.value);

    const select = form?.querySelector('select[name="id"]');
    if (select?.value) return String(select.value);

    const hidden = form?.querySelector('input[name="id"]');
    if (hidden?.value) return String(hidden.value);

    try {
      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      if (urlVariant) return String(urlVariant);
    } catch (error) {}

    return lastVariantId || pricingData.selectedVariantId || '';
  }

  function getPriceElement() {
    return (
      document.querySelector('.price .price-item.price-item--sale') ||
      document.querySelector('.price .price-item.price-item--regular') ||
      document.querySelector('.price__regular .price-item--regular') ||
      document.querySelector('.price-item--regular')
    );
  }

  function parseDomUnitPrice() {
    const raw = getPriceElement()?.textContent || '';
    const match = raw
      .replace(/\s+/g, ' ')
      .match(/[-+]?\d{1,3}(?:,\d{3})*(?:\.\d+)?|[-+]?\d+(?:\.\d+)?/);

    if (!match) return null;

    const value = parseFloat(match[0].replace(/,/g, ''));
    return Number.isFinite(value) ? value : null;
  }

  function getVariantUnitPrice() {
    const variantId = getCurrentVariantId();

    if (variantId) {
      const cents = Number(pricingData.variantPrices?.[variantId]);

      if (Number.isFinite(cents)) {
        lastVariantId = variantId;
        lastUnitPrice = cents / 100;
        return lastUnitPrice;
      }
    }

    /*
     * Fallback only. Shopify's visible price can briefly be blank/zero
     * while a variant is changing, so never replace a known good unit
     * price with that temporary state.
     */
    const domPrice = parseDomUnitPrice();

    if (Number.isFinite(domPrice) && domPrice > 0) {
      lastUnitPrice = domPrice;
      return domPrice;
    }

    return lastUnitPrice;
  }

  function currencyPrefix() {
    const raw = getPriceElement()?.textContent || '';
    const match = raw.match(/[-+]?\d/);
    return match ? raw.slice(0, match.index).trim() || '$' : '$';
  }

  function quantityInput() {
    const form = getProductForm();

    return (
      form?.querySelector('input[name="quantity"]') ||
      document.querySelector('.quantity__input')
    );
  }

  function getBestKnownSquareFeet() {
    if (currentSquareFeet > 0) return currentSquareFeet;

    const calculatorSquareFeet = Number(
      window.AlwanMediaCalculator?.getSquareFeet?.()
    );

    if (Number.isFinite(calculatorSquareFeet) && calculatorSquareFeet > 0) {
      currentSquareFeet = Math.floor(calculatorSquareFeet);
    }

    return currentSquareFeet;
  }

  function syncQuantity() {
    const squareFeet = getBestKnownSquareFeet();
    if (squareFeet < 1) return;

    const qty = quantityInput();
    if (!qty) return;

    const next = String(squareFeet);

    if (qty.value === next) return;

    qty.value = next;
    qty.dispatchEvent(new Event('input', { bubbles: true }));
    qty.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function renderTotal() {
    const target = document.getElementById('totalPrice');
    if (!target) return;

    const squareFeet = getBestKnownSquareFeet();
    const unitPrice = getVariantUnitPrice();
    const prefix = currencyPrefix();

    /*
     * Do not flash/reset to $0 during a variant transition when the
     * calculator still has a valid square-foot amount.
     */
    if (squareFeet < 1) {
      target.textContent = `${prefix}0.00`;
      return;
    }

    if (!Number.isFinite(unitPrice)) {
      return;
    }

    target.textContent = `${prefix}${(unitPrice * squareFeet).toFixed(2)}`;
  }

  function syncPricingState() {
    syncQuantity();
    renderTotal();
  }

  function applySquareFootage(squareFeet) {
    const value = parseInt(squareFeet, 10);

    if (Number.isFinite(value) && value > 0) {
      currentSquareFeet = value;
    } else {
      currentSquareFeet = 0;
    }

    if (currentSquareFeet > 0) {
      syncQuantity();
    }

    renderTotal();

    document.dispatchEvent(
      new CustomEvent('alwan:pricing:update', {
        detail: { squareFeet: currentSquareFeet }
      })
    );
  }

  document.addEventListener('alwan:calculator:update', (event) => {
    const valid = event.detail?.valid;
    const squareFeet = parseInt(event.detail?.squareFeet, 10);

    /*
     * A genuine invalid/cleared calculator state should still clear pricing.
     * A variant transition itself never calls this with zero; the calculator
     * will immediately republish its preserved dimensions/square footage.
     */
    applySquareFootage(
      valid && Number.isFinite(squareFeet) && squareFeet > 0
        ? squareFeet
        : 0
    );
  });

  function variantChanged(eventOrPayload) {
    /*
     * Capture the new variant directly from whichever event source fired.
     * This lets the total update from Liquid's variant-price map immediately,
     * without waiting for the theme's visible price DOM to finish repainting.
     */
    const eventVariantId =
      eventOrPayload?.data?.variant?.id ||
      eventOrPayload?.detail?.variant?.id ||
      eventOrPayload?.detail?.id ||
      (eventOrPayload?.target?.matches?.('input[name="id"], select[name="id"]')
        ? eventOrPayload.target.value
        : '');

    if (eventVariantId) {
      lastVariantId = String(eventVariantId);
    }

    /*
     * Variant changes should ONLY change the per-square-foot unit price.
     * Shopify themes frequently reset the quantity input while swapping the
     * variant, so reassert the calculator quantity after the theme settles.
     * Multiple short passes cover Dawn/pub-sub and custom variant selectors.
     */
    [0, 40, 100, 200, 400, 800].forEach((delay, index) => {
      window.setTimeout(() => {
        loadPricingData();
        syncPricingState();

        if (index === 1) {
          document.dispatchEvent(new CustomEvent('alwan:variant:change'));
        }
      }, delay);
    });
  }

  document.addEventListener('change', (event) => {
    if (event.target.matches('input[name="id"], select[name="id"]')) {
      variantChanged(event);
    }
  });

  document.addEventListener('variant:change', variantChanged);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    try {
      subscribe(PUB_SUB_EVENTS.variantChange, variantChanged);
    } catch (error) {}
  }

  window.AlwanPricing = {
    applySquareFootage,
    renderTotal,
    sync: syncPricingState,
    getSquareFeet: () => currentSquareFeet,
    getVariantId: getCurrentVariantId
  };

  loadPricingData();

  /*
   * Do not force a blank/zero quantity on boot. The calculator will publish
   * its value once initialized; until then, leave Shopify's existing quantity
   * untouched.
   */
  renderTotal();
})(window, document);
